import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import Base from './Base';
import Swal from 'sweetalert2';

const DesignerOnboarding = () => {
    const { authTokens } = useContext(AuthContext);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        pan: '',
        bank_account_number: '',
        ifsc_code: '',
        gstin: '',
        business_type: 'individual'
    });
    const [loading, setLoading] = useState(false);
    const [existingProfile, setExistingProfile] = useState(null);
    const [profileLoading, setProfileLoading] = useState(true);

    const businessTypeOptions = [
        { value: 'individual', label: 'Individual' },
        { value: 'proprietorship', label: 'Proprietorship' },
        { value: 'company', label: 'Company' },
        { value: 'partnership', label: 'Partnership' }
    ];

    useEffect(() => {
        checkExistingProfile();
    }, []);

    const checkExistingProfile = async () => {
        try {
            const response = await axios.get('https://vasstrinfo.com/api/designer/profile/', {
                headers: {
                    'Authorization': `Bearer ${authTokens?.access}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                setExistingProfile(response.data.data);
                setFormData({
                    name: response.data.data.name || '',
                    email: response.data.data.email || '',
                    phone: response.data.data.phone || '',
                    pan: '', // Don't pre-fill sensitive data
                    bank_account_number: '', // Don't pre-fill sensitive data
                    ifsc_code: response.data.data.ifsc_code || '',
                    gstin: response.data.data.gstin || '',
                    business_type: response.data.data.business_type || 'individual'
                });
            }
        } catch (error) {
            // Profile doesn't exist yet, that's fine
            console.log('No existing profile found');
        } finally {
            setProfileLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value.toUpperCase().includes(name.toUpperCase()) &&
                   ['pan', 'ifsc_code', 'gstin'].includes(name)
                   ? value.toUpperCase()
                   : value
        }));
    };

    const validateForm = () => {
        // Required field validation
        const requiredFields = ['name', 'email', 'phone', 'pan', 'bank_account_number', 'ifsc_code'];
        for (let field of requiredFields) {
            if (!formData[field].trim()) {
                Swal.fire('Error', `${field.replace('_', ' ').toUpperCase()} is required`, 'error');
                return false;
            }
        }

        // PAN validation
        const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
        if (!panRegex.test(formData.pan)) {
            Swal.fire('Error', 'Please enter a valid PAN number (e.g., ABCDE1234F)', 'error');
            return false;
        }

        // IFSC validation
        const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
        if (!ifscRegex.test(formData.ifsc_code)) {
            Swal.fire('Error', 'Please enter a valid IFSC code (e.g., ICIC0001234)', 'error');
            return false;
        }

        // GSTIN validation (if provided)
        if (formData.gstin) {
            const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
            if (!gstinRegex.test(formData.gstin)) {
                Swal.fire('Error', 'Please enter a valid GSTIN', 'error');
                return false;
            }
        }

        // Phone validation
        if (!/^[0-9]{10}$/.test(formData.phone.replace(/\s+/g, ''))) {
            Swal.fire('Error', 'Please enter a valid 10-digit phone number', 'error');
            return false;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            Swal.fire('Error', 'Please enter a valid email address', 'error');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setLoading(true);

        try {
            const endpoint = existingProfile ?
                'https://vasstrinfo.com/api/designer/profile/update/' :
                'https://vasstrinfo.com/api/designer/onboarding/';

            const method = existingProfile ? 'PUT' : 'POST';

            const response = await axios({
                method: method,
                url: endpoint,
                data: formData,
                headers: {
                    'Authorization': `Bearer ${authTokens?.access}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success) {
                Swal.fire({
                    title: 'Success!',
                    text: existingProfile ?
                        'Your profile has been updated successfully.' :
                        'Your designer profile has been submitted successfully. Your account is now under review.',
                    icon: 'success',
                    confirmButtonText: 'OK'
                }).then(() => {
                    checkExistingProfile(); // Refresh profile data
                });
            } else {
                Swal.fire('Error', response.data.message || 'Submission failed', 'error');
            }
        } catch (error) {
            console.error('Submission error:', error);
            console.error('Error details:', error.response?.data);
            console.error('Error status:', error.response?.status);
            const errorMessage = error.response?.data?.message || error.response?.data?.detail || error.message || 'An error occurred during submission';
            Swal.fire('Error', errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'approved':
                return 'bg-green-100 text-green-800 border border-green-200';
            case 'rejected':
                return 'bg-red-100 text-red-800 border border-red-200';
            case 'under_review':
                return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
            default:
                return 'bg-gray-100 text-gray-800 border border-gray-200';
        }
    };

    if (profileLoading) {
        return (
            <Base Content={
                <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-orange-100">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-orange-500 mx-auto mb-4"></div>
                        <p className="text-orange-600 text-lg font-medium">Loading profile...</p>
                    </div>
                </div>
            } />
        );
    }

    return (
        <Base Content={
            <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100 py-8 px-4">
                <div className="max-w-4xl mx-auto">
                    <div className="bg-white rounded-xl shadow-lg p-8 border border-orange-200">
                        <div className="mb-8 text-center">
                            <h1 className="text-4xl font-bold text-orange-500 mb-3">Designer KYC Verification</h1>
                            <p className="text-gray-700 text-lg">
                                Complete your KYC details to start receiving payouts for your products
                            </p>
                        </div>

                        {existingProfile && (
                            <div className="mb-8 p-6 bg-orange-50 border border-orange-200 rounded-xl">
                                <div className="flex items-center justify-between mb-4">
                                    <div>
                                        <h3 className="text-xl font-bold text-orange-600">Profile Status</h3>
                                        <p className="text-orange-700 text-lg">Your designer profile has been submitted</p>
                                    </div>
                                    <span className={`px-4 py-2 rounded-full text-sm font-bold ${getStatusBadgeClass(existingProfile.onboarding_status)}`}>
                                        {existingProfile.onboarding_status.charAt(0).toUpperCase() + existingProfile.onboarding_status.slice(1).replace('_', ' ')}
                                    </span>
                                </div>

                                {existingProfile.bank_details_submitted && (
                                    <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                                        <p className="text-yellow-800 font-medium">
                                            <strong>Note:</strong> Bank details cannot be modified once submitted. Contact admin for changes.
                                        </p>
                                    </div>
                                )}

                                {existingProfile.admin_notes && (
                                    <div className="p-4 bg-white rounded-lg border border-orange-100">
                                        <p className="text-gray-700">
                                            <span className="font-bold text-orange-600">Admin Notes:</span> {existingProfile.admin_notes}
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-8">
                            {/* Personal Information */}
                            <div className="space-y-6">
                                <h2 className="text-2xl font-bold text-orange-500 border-b-2 border-orange-300 pb-3">Personal Information</h2>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-lg font-bold text-orange-500 mb-2">
                                            Full Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            className="w-full p-3 border border-orange-300 rounded-lg bg-[#fffaf3] text-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                                            placeholder="Enter your full name"
                                            required
                                            disabled={existingProfile && existingProfile.onboarding_status === 'approved'}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-lg font-bold text-orange-500 mb-2">
                                            Email Address <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            className="w-full p-3 border border-orange-300 rounded-lg bg-[#fffaf3] text-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                                            placeholder="Enter your email"
                                            required
                                            disabled={existingProfile && existingProfile.onboarding_status === 'approved'}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-lg font-bold text-orange-500 mb-2">
                                            Phone Number <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            className="w-full p-3 border border-orange-300 rounded-lg bg-[#fffaf3] text-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                                            placeholder="10-digit phone number"
                                            required
                                            disabled={existingProfile && existingProfile.onboarding_status === 'approved'}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-lg font-bold text-orange-500 mb-2">
                                            Business Type
                                        </label>
                                        <select
                                            name="business_type"
                                            value={formData.business_type}
                                            onChange={handleInputChange}
                                            className="w-full p-3 border border-orange-300 rounded-lg bg-[#fffaf3] text-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                                            disabled={existingProfile && existingProfile.onboarding_status === 'approved'}
                                        >
                                            {businessTypeOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* KYC Information */}
                            <div className="space-y-6">
                                <h2 className="text-2xl font-bold text-orange-500 border-b-2 border-orange-300 pb-3">KYC Information</h2>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-lg font-bold text-orange-500 mb-2">
                                            PAN Number <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="pan"
                                            value={formData.pan}
                                            onChange={handleInputChange}
                                            className="w-full p-3 border border-orange-300 rounded-lg bg-[#fffaf3] text-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                                            placeholder="ABCDE1234F"
                                            maxLength="10"
                                            required
                                            disabled={existingProfile && existingProfile.bank_details_submitted}
                                        />
                                        {existingProfile && existingProfile.pan_masked && (
                                            <p className="text-sm text-orange-600 mt-2 font-medium">
                                                Current PAN: {existingProfile.pan_masked}
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-lg font-bold text-orange-500 mb-2">
                                            GSTIN (Optional)
                                        </label>
                                        <input
                                            type="text"
                                            name="gstin"
                                            value={formData.gstin}
                                            onChange={handleInputChange}
                                            className="w-full p-3 border border-orange-300 rounded-lg bg-[#fffaf3] text-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                                            placeholder="22AAAAA0000A1Z5"
                                            maxLength="15"
                                            disabled={existingProfile && existingProfile.onboarding_status === 'approved'}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Bank Information */}
                            <div className="space-y-6">
                                <h2 className="text-2xl font-bold text-orange-500 border-b-2 border-orange-300 pb-3">Bank Information</h2>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-lg font-bold text-orange-500 mb-2">
                                            Bank Account Number <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="bank_account_number"
                                            value={formData.bank_account_number}
                                            onChange={handleInputChange}
                                            className="w-full p-3 border border-orange-300 rounded-lg bg-[#fffaf3] text-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                                            placeholder="Enter bank account number"
                                            required
                                            disabled={existingProfile && existingProfile.bank_details_submitted}
                                        />
                                        {existingProfile && existingProfile.bank_account_masked && (
                                            <p className="text-sm text-orange-600 mt-2 font-medium">
                                                Current Account: {existingProfile.bank_account_masked}
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-lg font-bold text-orange-500 mb-2">
                                            IFSC Code <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="ifsc_code"
                                            value={formData.ifsc_code}
                                            onChange={handleInputChange}
                                            className="w-full p-3 border border-orange-300 rounded-lg bg-[#fffaf3] text-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                                            placeholder="ICIC0001234"
                                            maxLength="11"
                                            required
                                            disabled={existingProfile && existingProfile.bank_details_submitted}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Security Notice */}
                            <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
                                <div className="flex items-start">
                                    <div className="flex-shrink-0">
                                        <svg className="h-6 w-6 text-orange-500" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <h3 className="text-lg font-bold text-orange-800">Security Notice</h3>
                                        <p className="text-orange-700 mt-2">
                                            Your sensitive information (PAN and bank details) will be encrypted and stored securely.
                                            This information is required for Razorpay account creation and payout processing.
                                            <strong> Bank details cannot be modified once submitted.</strong>
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Submit Button */}
                            {(!existingProfile || existingProfile.onboarding_status === 'rejected') && (
                                <div className="pt-4">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full bg-orange-500 text-white py-4 px-6 rounded-lg hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-400 disabled:opacity-50 disabled:cursor-not-allowed font-bold text-lg transition-colors duration-200"
                                    >
                                        {loading ? (
                                            <div className="flex items-center justify-center">
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                                                {existingProfile ? 'Updating...' : 'Submitting...'}
                                            </div>
                                        ) : (
                                            existingProfile ? 'Update Profile' : 'Submit for Review'
                                        )}
                                    </button>
                                </div>
                            )}

                            {existingProfile && !['rejected', 'pending'].includes(existingProfile.onboarding_status) && (
                                <div className="pt-4">
                                    <button
                                        type="submit"
                                        disabled={loading || existingProfile.bank_details_submitted}
                                        className="w-full bg-orange-500 text-white py-4 px-6 rounded-lg hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-400 disabled:opacity-50 disabled:cursor-not-allowed font-bold text-lg transition-colors duration-200"
                                    >
                                        {existingProfile.bank_details_submitted ? 'Profile Locked' : 'Update Details'}
                                    </button>
                                </div>
                            )}
                        </form>
                    </div>
                </div>
            </div>
        } />
    );
};

export default DesignerOnboarding;